import { FieldValues } from "react-hook-form";

export const getDirtyValues = async <T extends FieldValues>(
  dirtyFields: Partial<Record<keyof T, any>>,
  allValues: T,
): Promise<Partial<T>> => {
  const result: Partial<T> = {};

  for (const keyString in dirtyFields) {
    if (Object.prototype.hasOwnProperty.call(dirtyFields, keyString)) {
      const key = keyString as keyof T;

      const dirtyValue = dirtyFields[key];
      const originalValue = allValues?.[key];

      if (!dirtyValue) {
        continue;
      }

      const isObject = typeof dirtyValue === 'object' && dirtyValue !== null;
      const isArray = Array.isArray(dirtyValue);

      if (isObject && !isArray) {
        const nestedDirtyValues = await getDirtyValues(dirtyValue, originalValue);

        if (Object.keys(nestedDirtyValues).length > 0) {
          result[key] = nestedDirtyValues as Partial<T>[keyof T];
        }
      } else if (isArray) {
        const dirtyArrayPromises = dirtyValue.map((fields, index) => {
          if (fields) {
            const originalArrayItemValues = originalValue[index];

            const dirtyArrayItemValuesPromise = getDirtyValues(fields, originalArrayItemValues);

            return dirtyArrayItemValuesPromise.then(dirtyArrayItemValues => {
              if (originalArrayItemValues && originalArrayItemValues.id) {
                return { ...dirtyArrayItemValues, id: originalArrayItemValues.id };
              }

              return dirtyArrayItemValues;
            });
          }

          return null;
        });

        const resolvedDirtyArrayValues = await Promise.all(dirtyArrayPromises);

        const dirtyArrayValues = resolvedDirtyArrayValues.filter(Boolean);

        result[key] = dirtyArrayValues as Partial<T>[keyof T];
      } else {
        result[key] = originalValue as Partial<T>[keyof T];
      }
    }
  }

  return result;
};
